import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { 
  X, User, Award, GraduationCap, Briefcase, MapPin, Calendar,
  Twitter, Linkedin, Instagram, Globe, Mail, Phone, Edit2, Save
} from 'lucide-react';
import { toast } from 'sonner';

import { API } from '../config/api';

export default function CoachBioModal({ coach, isOpen, onClose, isOwnProfile = false }) {
  const [bio, setBio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    headline: '',
    bio_text: '',
    qualifications: [],
    certifications: [],
    experience_years: '',
    specializations: [],
    location: '',
    contact_email: '',
    contact_phone: '',
    social_twitter: '',
    social_linkedin: '',
    social_instagram: '',
    website: ''
  });
  const [newQualification, setNewQualification] = useState('');
  const [newCertification, setNewCertification] = useState('');
  const [newSpecialization, setNewSpecialization] = useState('');

  const fetchBio = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/coach-bio/${coach.id}`);
      setBio(res.data);
      setFormData({
        headline: res.data.headline || '',
        bio_text: res.data.bio_text || '',
        qualifications: res.data.qualifications || [],
        certifications: res.data.certifications || [],
        experience_years: res.data.experience_years || '',
        specializations: res.data.specializations || [],
        location: res.data.location || '',
        contact_email: res.data.contact_email || '',
        contact_phone: res.data.contact_phone || '',
        social_twitter: res.data.social_twitter || '',
        social_linkedin: res.data.social_linkedin || '',
        social_instagram: res.data.social_instagram || '',
        website: res.data.website || ''
      });
    } catch (err) {
      console.error('Failed to fetch coach bio:', err);
    } finally {
      setLoading(false);
    }
  }, [coach?.id]);

  useEffect(() => {
    if (isOpen && coach?.id) {
      fetchBio();
    }
  }, [isOpen, coach?.id, fetchBio]);

  const handleSave = async () => {
    try {
      await axios.post(`${API}/coach-bio/${coach.id}`, formData);
      toast.success('Biography updated!');
      setEditing(false);
      fetchBio();
    } catch (err) {
      toast.error('Failed to save biography');
    }
  };

  const addItem = (field, value, setter) => {
    if (value.trim()) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], value.trim()]
      }));
      setter('');
    }
  };

  const removeItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="coach-bio-modal">
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="relative">
          <div className="h-24 bg-gradient-to-r from-[#02b5ff] to-[#ff025f]" />
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-2 bg-black/50 rounded-full text-white hover:bg-black/70"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="absolute -bottom-12 left-6">
            <div className="w-24 h-24 rounded-full border-4 border-zinc-900 overflow-hidden bg-zinc-800">
              {coach?.photo_url ? (
                <img src={coach.photo_url} alt={coach.full_name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <User className="h-10 w-10 text-zinc-500" />
                </div>
              )}
            </div>
          </div>
          {isOwnProfile && (
            <button
              onClick={() => setEditing(!editing)}
              className="absolute -bottom-4 right-4 px-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded-full text-sm text-white hover:bg-zinc-700 flex items-center gap-1"
            >
              <Edit2 className="h-3 w-3" /> {editing ? 'Cancel' : 'Edit'}
            </button>
          )}
        </div>

        {/* Content */}
        <div className="pt-16 px-6 pb-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {loading ? (
            <div className="animate-pulse space-y-4">
              <div className="h-6 bg-zinc-800 rounded w-48" />
              <div className="h-4 bg-zinc-800 rounded w-full" />
              <div className="h-4 bg-zinc-800 rounded w-3/4" />
            </div>
          ) : editing ? (
            /* Edit Mode */
            <div className="space-y-4">
              <div>
                <label className="text-xs text-zinc-400 mb-1 block">Name</label>
                <div className="text-xl font-bold text-white">{coach?.full_name}</div>
              </div>
              
              <div>
                <label className="text-xs text-zinc-400 mb-1 block">Headline</label>
                <input
                  type="text"
                  value={formData.headline}
                  onChange={(e) => setFormData({ ...formData, headline: e.target.value })}
                  placeholder="e.g., UEFA A Licensed Coach | Youth Development Specialist"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>

              <div>
                <label className="text-xs text-zinc-400 mb-1 block">Biography</label>
                <textarea
                  value={formData.bio_text}
                  onChange={(e) => setFormData({ ...formData, bio_text: e.target.value })}
                  placeholder="Tell parents and athletes about yourself..."
                  rows={4}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm resize-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">Years of Experience</label>
                  <input
                    type="number"
                    value={formData.experience_years}
                    onChange={(e) => setFormData({ ...formData, experience_years: e.target.value })}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">Location</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="e.g., Manchester, UK"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                  />
                </div>
              </div>

              {/* Qualifications */}
              <div>
                <label className="text-xs text-zinc-400 mb-1 block">Qualifications</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newQualification}
                    onChange={(e) => setNewQualification(e.target.value)}
                    placeholder="e.g., BSc Sports Science"
                    className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addItem('qualifications', newQualification, setNewQualification))}
                  />
                  <Button size="sm" onClick={() => addItem('qualifications', newQualification, setNewQualification)}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.qualifications.map((q, i) => (
                    <span key={i} className="bg-zinc-800 text-zinc-300 px-2 py-1 rounded text-xs flex items-center gap-1">
                      {q} <button onClick={() => removeItem('qualifications', i)} className="text-zinc-500 hover:text-red-400">×</button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Certifications */}
              <div>
                <label className="text-xs text-zinc-400 mb-1 block">Certifications</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newCertification}
                    onChange={(e) => setNewCertification(e.target.value)}
                    placeholder="e.g., UEFA A License"
                    className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addItem('certifications', newCertification, setNewCertification))}
                  />
                  <Button size="sm" onClick={() => addItem('certifications', newCertification, setNewCertification)}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.certifications.map((c, i) => (
                    <span key={i} className="bg-[#02b5ff]/20 text-[#02b5ff] px-2 py-1 rounded text-xs flex items-center gap-1">
                      {c} <button onClick={() => removeItem('certifications', i)} className="text-[#02b5ff]/50 hover:text-red-400">×</button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Specializations */}
              <div>
                <label className="text-xs text-zinc-400 mb-1 block">Specializations</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newSpecialization}
                    onChange={(e) => setNewSpecialization(e.target.value)}
                    placeholder="e.g., Youth Development"
                    className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addItem('specializations', newSpecialization, setNewSpecialization))}
                  />
                  <Button size="sm" onClick={() => addItem('specializations', newSpecialization, setNewSpecialization)}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.specializations.map((s, i) => (
                    <span key={i} className="bg-[#ff025f]/20 text-[#ff025f] px-2 py-1 rounded text-xs flex items-center gap-1">
                      {s} <button onClick={() => removeItem('specializations', i)} className="text-[#ff025f]/50 hover:text-red-400">×</button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Contact & Social */}
              <div className="border-t border-zinc-800 pt-4 mt-4">
                <h4 className="text-sm font-semibold text-white mb-3">Contact & Social</h4>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    placeholder="Email"
                    className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                  />
                  <input
                    type="tel"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    placeholder="Phone"
                    className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                  />
                  <input
                    type="text"
                    value={formData.social_twitter}
                    onChange={(e) => setFormData({ ...formData, social_twitter: e.target.value })}
                    placeholder="Twitter/X handle"
                    className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                  />
                  <input
                    type="text"
                    value={formData.social_linkedin}
                    onChange={(e) => setFormData({ ...formData, social_linkedin: e.target.value })}
                    placeholder="LinkedIn URL"
                    className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                  />
                  <input
                    type="text"
                    value={formData.social_instagram}
                    onChange={(e) => setFormData({ ...formData, social_instagram: e.target.value })}
                    placeholder="Instagram handle"
                    className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                  />
                  <input
                    type="text"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="Website URL"
                    className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                  />
                </div>
              </div>

              <Button onClick={handleSave} className="w-full bg-[#02b5ff] text-white mt-4">
                <Save className="h-4 w-4 mr-2" /> Save Biography
              </Button>
            </div>
          ) : (
            /* View Mode */
            <div className="space-y-4">
              <div>
                <h2 className="text-2xl font-bold text-white">{coach?.full_name}</h2>
                {bio?.headline && (
                  <p className="text-zinc-400 mt-1">{bio.headline}</p>
                )}
              </div>

              {/* Quick Stats */}
              <div className="flex flex-wrap gap-3">
                {bio?.experience_years && (
                  <div className="flex items-center gap-2 bg-zinc-800/50 px-3 py-2 rounded-lg">
                    <Briefcase className="h-4 w-4 text-[#02b5ff]" />
                    <span className="text-sm text-white">{bio.experience_years}+ years experience</span>
                  </div>
                )}
                {bio?.location && (
                  <div className="flex items-center gap-2 bg-zinc-800/50 px-3 py-2 rounded-lg">
                    <MapPin className="h-4 w-4 text-[#ff025f]" />
                    <span className="text-sm text-white">{bio.location}</span>
                  </div>
                )}
              </div>

              {/* Bio Text */}
              {bio?.bio_text && (
                <div>
                  <h4 className="text-sm font-semibold text-zinc-400 mb-2">About</h4>
                  <p className="text-zinc-300 text-sm leading-relaxed">{bio.bio_text}</p>
                </div>
              )}

              {/* Qualifications */}
              {bio?.qualifications?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-zinc-400 mb-2 flex items-center gap-2">
                    <GraduationCap className="h-4 w-4" /> Qualifications
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {bio.qualifications.map((q, i) => (
                      <span key={i} className="bg-zinc-800 text-zinc-300 px-3 py-1 rounded-full text-sm">{q}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Certifications */}
              {bio?.certifications?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-zinc-400 mb-2 flex items-center gap-2">
                    <Award className="h-4 w-4" /> Certifications
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {bio.certifications.map((c, i) => (
                      <span key={i} className="bg-[#02b5ff]/20 text-[#02b5ff] px-3 py-1 rounded-full text-sm font-medium">{c}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Specializations */}
              {bio?.specializations?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-zinc-400 mb-2">Specializations</h4>
                  <div className="flex flex-wrap gap-2">
                    {bio.specializations.map((s, i) => (
                      <span key={i} className="bg-[#ff025f]/20 text-[#ff025f] px-3 py-1 rounded-full text-sm">{s}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Contact & Social Links */}
              {(bio?.contact_email || bio?.social_twitter || bio?.social_linkedin || bio?.social_instagram || bio?.website) && (
                <div className="border-t border-zinc-800 pt-4 mt-4">
                  <h4 className="text-sm font-semibold text-zinc-400 mb-3">Connect</h4>
                  <div className="flex flex-wrap gap-3">
                    {bio?.contact_email && (
                      <a href={`mailto:${bio.contact_email}`} className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 px-3 py-2 rounded-lg transition-colors">
                        <Mail className="h-4 w-4 text-zinc-400" />
                        <span className="text-sm text-white">Email</span>
                      </a>
                    )}
                    {bio?.social_twitter && (
                      <a href={`https://twitter.com/${bio.social_twitter.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 px-3 py-2 rounded-lg transition-colors">
                        <Twitter className="h-4 w-4 text-[#1DA1F2]" />
                        <span className="text-sm text-white">Twitter</span>
                      </a>
                    )}
                    {bio?.social_linkedin && (
                      <a href={bio.social_linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 px-3 py-2 rounded-lg transition-colors">
                        <Linkedin className="h-4 w-4 text-[#0A66C2]" />
                        <span className="text-sm text-white">LinkedIn</span>
                      </a>
                    )}
                    {bio?.social_instagram && (
                      <a href={`https://instagram.com/${bio.social_instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 px-3 py-2 rounded-lg transition-colors">
                        <Instagram className="h-4 w-4 text-[#E4405F]" />
                        <span className="text-sm text-white">Instagram</span>
                      </a>
                    )}
                    {bio?.website && (
                      <a href={bio.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 px-3 py-2 rounded-lg transition-colors">
                        <Globe className="h-4 w-4 text-green-500" />
                        <span className="text-sm text-white">Website</span>
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* No bio yet message */}
              {!bio && !isOwnProfile && (
                <div className="text-center py-8 text-zinc-500">
                  <User className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>This coach hasn't added their biography yet.</p>
                </div>
              )}

              {!bio && isOwnProfile && (
                <div className="text-center py-8">
                  <User className="h-12 w-12 mx-auto mb-3 text-zinc-500" />
                  <p className="text-zinc-400 mb-4">Add your biography to help parents and athletes learn about you.</p>
                  <Button onClick={() => setEditing(true)} className="bg-[#02b5ff]">
                    <Edit2 className="h-4 w-4 mr-2" /> Add Biography
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
